<template>
  <div class="time-entries">
    <!-- Summary -->
    <div class="time-summary">
      <div class="summary-item">
        <span class="summary-label">{{ s__('TimeEntries|My time') }}:</span>
        <span class="summary-value">{{ formatDuration(myTimeSpent) }}</span>
      </div>
      <div v-if="isAdminOrOwner && totalTimeSpent !== myTimeSpent" class="summary-item">
        <span class="summary-label">{{ s__('TimeEntries|Total') }}:</span>
        <span class="summary-value">{{ formatDuration(totalTimeSpent) }}</span>
      </div>
      <div v-if="totalTimeSpentInDescendants > 0" class="summary-item">
        <span class="summary-label">{{ s__('TimeEntries|In children tasks') }}:</span>
        <span class="summary-value">{{ formatDuration(totalTimeSpentInDescendants) }}</span>
      </div>
    </div>

    <!-- Add Time Entry Form -->
    <div v-if="showAddForm" class="add-time-form">
      <div class="form-row">
        <input
          v-model="newEntry.duration"
          type="text"
          :placeholder="s__('TimeEntries|Duration (e.g. 2h30m, 1:30, 90m)')"
          class="duration-input"
          @keydown.enter="submitEntry"
        />
      </div>
      <div class="form-row">
        <input v-model="newEntry.workedOn" type="date" class="date-input" />
        <div v-if="newEntry.workedOn" class="date-preview">
          <FormattedDateTime
            :value="newEntry.workedOn + 'T00:00:00'"
            :locale="locale"
            :timezone="timezone"
            format="dateLong"
          />
        </div>
      </div>
      <div class="form-row">
        <input
          v-model="newEntry.description"
          type="text"
          :placeholder="s__('TimeEntries|Description (optional)')"
          class="description-input"
        />
      </div>
      <div class="form-actions">
        <button
          :disabled="submitting || !newEntry.duration"
          class="btn-sm btn-primary"
          @click="submitEntry"
        >
          {{ submitting ? s__('TimeEntries|Saving...') : s__('TimeEntries|Log Time') }}
        </button>
        <button class="btn-sm btn-secondary" @click="cancelAdd">{{ __('Cancel') }}</button>
      </div>
      <div v-if="formError" class="error-message">{{ formError }}</div>
    </div>
    <button v-else class="add-time-btn mobile-stretch-btn" @click="openAddForm">
      + {{ s__('TimeEntries|Log Time') }}
    </button>

    <!-- Time Entries List -->
    <div v-if="entries.length > 0" class="entries-list">
      <div class="entries-section-header">
        <span class="section-title">{{ s__('TimeEntries|This task') }}</span>
        <span class="section-total">{{ formatDuration(myTimeSpent) }}</span>
      </div>
      <div v-for="entry in entries" :key="entry.id" class="entry-item entry-item-this-task">
        <div class="entry-main">
          <span class="entry-duration">{{ entry.formattedDuration }}</span>
          <span class="entry-date"
            ><FormattedDateTime
              :value="entry.workedOn + 'T00:00:00'"
              :locale="locale"
              :timezone="timezone"
              format="date"
          /></span>
          <span v-if="isAdminOrOwner && entry.user" class="entry-user"
            >@{{ entry.user.username }}</span
          >
        </div>
        <div v-if="entry.description" class="entry-description">{{ entry.description }}</div>
        <div v-if="entry.canUpdate || entry.canDestroy" class="entry-actions">
          <button
            v-if="entry.canUpdate"
            class="btn-icon"
            :title="__('Edit')"
            @click="startEdit(entry)"
          >
            <EditIcon />
          </button>
          <button
            v-if="entry.canDestroy"
            class="btn-icon"
            :title="__('Delete')"
            @click="deleteEntry(entry)"
          >
            <TrashIcon />
          </button>
        </div>

        <!-- Edit Form -->
        <div v-if="editingEntryId === entry.id" class="edit-time-form">
          <div class="form-row">
            <input
              v-model="editEntry.duration"
              type="text"
              :placeholder="s__('TimeEntries|Duration')"
              class="duration-input"
            />
          </div>
          <div class="form-row">
            <input v-model="editEntry.workedOn" type="date" class="date-input" />
            <div v-if="editEntry.workedOn" class="date-preview">
              <FormattedDateTime
                :value="editEntry.workedOn + 'T00:00:00'"
                :locale="locale"
                :timezone="timezone"
                format="dateLong"
              />
            </div>
          </div>
          <div class="form-row">
            <input
              v-model="editEntry.description"
              type="text"
              :placeholder="s__('TimeEntries|Description')"
              class="description-input"
            />
          </div>
          <div class="form-actions">
            <button :disabled="submitting" class="btn-sm btn-primary" @click="updateEntry">
              {{ submitting ? s__('TimeEntries|Saving...') : __('Save') }}
            </button>
            <button class="btn-sm btn-secondary" @click="cancelEdit">{{ __('Cancel') }}</button>
          </div>
          <div v-if="editError" class="error-message">{{ editError }}</div>
        </div>
      </div>
    </div>
    <div v-else class="empty-state">
      {{ s__('TimeEntries|No time logged yet') }}
    </div>

    <!-- Children Tasks Time Entries -->
    <div v-if="totalTimeSpentInDescendants > 0" class="children-time-summary">
      <div class="children-summary-header">
        <span class="section-title">{{ s__('TimeEntries|In children tasks') }}</span>
        <span class="section-total">{{ formatDuration(totalTimeSpentInDescendants) }}</span>
      </div>
      <div v-if="childTasksWithEntries.length > 0" class="children-entries-list">
        <TimeEntriesHierarchy
          v-for="childTask in childTasksWithEntries"
          :key="childTask.id"
          :task="childTask"
          :depth="0"
          :project-id="projectId"
          :chat-id="chatId"
          :from-id="fromId"
          :locale="locale"
          :timezone="timezone"
          :is-admin-or-owner="isAdminOrOwner"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { s__, __ } from '~/locale'
import FormattedDateTime from '../../common/FormattedDateTime.vue'
import TimeEntriesHierarchy from './TimeEntriesHierarchy.vue'
import EditIcon from '../../common/icons/EditIcon.vue'
import TrashIcon from '../../common/icons/TrashIcon.vue'

const props = defineProps({
  taskId: { type: [String, Number], required: true },
  projectId: { type: [String, Number], required: true },
  chatId: { type: [String, Number], default: null },
  fromId: { type: [String, Number], default: null },
  entries: { type: Array, default: () => [] },
  totalTimeSpent: { type: Number, default: 0 },
  myTimeSpent: { type: Number, default: 0 },
  totalTimeSpentInDescendants: { type: Number, default: 0 },
  childTasks: { type: Array, default: () => [] },
  isAdminOrOwner: { type: Boolean, default: false },
  locale: { type: String, default: null },
  timezone: { type: String, default: null },
})

const emit = defineEmits(['time-logged', 'time-updated', 'time-deleted'])

const showAddForm = ref(false)
const submitting = ref(false)
const formError = ref(null)
const editingEntryId = ref(null)
const editError = ref(null)

function getLocalDateString() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const newEntry = ref({
  duration: '',
  workedOn: '',
  description: '',
})

function openAddForm() {
  newEntry.value.workedOn = getLocalDateString()
  showAddForm.value = true
}

const editEntry = ref({
  duration: '',
  workedOn: '',
  description: '',
})

const CREATE_TIME_ENTRY = gql`
  mutation CreateTimeEntry(
    $taskId: ID!
    $duration: String!
    $workedOn: ISO8601Date
    $description: String
  ) {
    createTimeEntry(
      taskId: $taskId
      duration: $duration
      workedOn: $workedOn
      description: $description
    ) {
      timeEntry {
        id
        durationMinutes
        formattedDuration
        workedOn
        description
        user {
          id
          username
        }
        canUpdate
        canDestroy
      }
      errors
    }
  }
`

const UPDATE_TIME_ENTRY = gql`
  mutation UpdateTimeEntry(
    $id: ID!
    $duration: String
    $workedOn: ISO8601Date
    $description: String
  ) {
    updateTimeEntry(id: $id, duration: $duration, workedOn: $workedOn, description: $description) {
      timeEntry {
        id
        durationMinutes
        formattedDuration
        workedOn
        description
        user {
          id
          username
        }
        canUpdate
        canDestroy
      }
      errors
    }
  }
`

const DESTROY_TIME_ENTRY = gql`
  mutation DestroyTimeEntry($id: ID!) {
    destroyTimeEntry(id: $id) {
      timeEntry {
        id
      }
      errors
    }
  }
`

const { mutate: createTimeEntry } = useMutation(CREATE_TIME_ENTRY)
const { mutate: updateTimeEntry } = useMutation(UPDATE_TIME_ENTRY)
const { mutate: destroyTimeEntry } = useMutation(DESTROY_TIME_ENTRY)

function formatDuration(minutes) {
  if (!minutes || minutes === 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`)
  return parts.join(' ')
}

// Filter child tasks that have time entries
const childTasksWithEntries = computed(() => {
  const filterTasksWithEntries = tasks => {
    return tasks
      .filter(task => {
        const hasEntries = task.timeEntries && task.timeEntries.length > 0
        const hasChildrenWithEntries =
          task.childTasks && filterTasksWithEntries(task.childTasks).length > 0
        return hasEntries || hasChildrenWithEntries
      })
      .map(task => ({
        ...task,
        childTasks: filterTasksWithEntries(task.childTasks || []),
      }))
  }
  return filterTasksWithEntries(props.childTasks || [])
})

async function submitEntry() {
  if (!newEntry.value.duration) return

  submitting.value = true
  formError.value = null

  try {
    const result = await createTimeEntry({
      taskId: String(props.taskId),
      duration: newEntry.value.duration,
      workedOn: newEntry.value.workedOn,
      description: newEntry.value.description || null,
    })

    if (result.data?.createTimeEntry?.errors?.length > 0) {
      formError.value = result.data.createTimeEntry.errors.join(', ')
    } else {
      const timeEntry = result.data.createTimeEntry.timeEntry
      // Reset form
      newEntry.value = {
        duration: '',
        workedOn: getLocalDateString(),
        description: '',
      }
      showAddForm.value = false
      // Emit the time entry data for optimistic update
      emit('time-logged', timeEntry)
    }
  } catch (err) {
    formError.value = err.message
  } finally {
    submitting.value = false
  }
}

function cancelAdd() {
  showAddForm.value = false
  newEntry.value = {
    duration: '',
    workedOn: getLocalDateString(),
    description: '',
  }
  formError.value = null
}

function startEdit(entry) {
  editingEntryId.value = entry.id
  editEntry.value = {
    duration: entry.formattedDuration,
    workedOn: entry.workedOn,
    description: entry.description || '',
  }
  editError.value = null
}

function cancelEdit() {
  editingEntryId.value = null
  editError.value = null
}

async function updateEntry() {
  if (!editEntry.value.duration) return

  submitting.value = true
  editError.value = null

  try {
    const result = await updateTimeEntry({
      id: String(editingEntryId.value),
      duration: editEntry.value.duration,
      workedOn: editEntry.value.workedOn,
      description: editEntry.value.description || null,
    })

    if (result.data?.updateTimeEntry?.errors?.length > 0) {
      editError.value = result.data.updateTimeEntry.errors.join(', ')
    } else {
      const timeEntry = result.data.updateTimeEntry.timeEntry
      editingEntryId.value = null
      // Emit the time entry data for optimistic update
      emit('time-updated', timeEntry)
    }
  } catch (err) {
    editError.value = err.message
  } finally {
    submitting.value = false
  }
}

async function deleteEntry(entry) {
  if (!confirm(s__('TimeEntries|Delete this time entry?'))) return

  try {
    const result = await destroyTimeEntry({ id: String(entry.id) })

    if (result.data?.destroyTimeEntry?.errors?.length > 0) {
      alert(result.data.destroyTimeEntry.errors.join(', '))
    } else {
      // Emit the time entry ID for optimistic update
      emit('time-deleted', entry.id)
    }
  } catch (err) {
    alert(err.message)
  }
}
</script>

<style scoped>
.time-entries {
  margin-top: 8px;
}

.time-summary {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 14px;
}

.summary-item {
  display: flex;
  gap: 4px;
}

.summary-label {
  color: #888;
}

.summary-value {
  font-weight: 600;
  color: #333;
}

.add-time-btn {
  background: #fff;
  border: 1px dashed #d1d5db;
  color: #6b7280;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  text-align: left;
  transition: all 0.15s ease-in-out;
}

.add-time-btn:hover {
  border-color: #2aabee;
  color: #2aabee;
  background: rgba(42, 171, 238, 0.05);
}

.add-time-form,
.edit-time-form {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  margin-bottom: 12px;
}

.form-row {
  margin-bottom: 8px;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  overflow: hidden;
}

.duration-input,
.date-input,
.description-input {
  display: block;
  width: 100%;
  inline-size: 100%;
  max-width: 100%;
  max-inline-size: 100%;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.date-input {
  -webkit-appearance: none;
  appearance: none;
}

/* iOS Safari keeps intrinsic width for date controls unless explicitly clamped */
@supports (-webkit-touch-callout: none) {
  .date-input {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    inline-size: 100% !important;
    max-inline-size: 100% !important;
  }
}

.date-preview {
  margin-top: 4px;
  font-size: 14px;
  background: #fff;
  color: #333;
  max-width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.date-preview :deep(time) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

/* Button styles inherit from forms.css */

.error-message {
  color: #dc2626;
  margin-top: 8px;
  font-size: 13px;
}

.entries-list {
  margin-top: 12px;
}

.entry-item {
  padding: 10px;
  border-bottom: 1px solid #eee;
  position: relative;
}

.entry-item:last-child {
  border-bottom: none;
}

.entry-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
  padding-right: 64px;
}

.entry-duration {
  font-weight: 600;
  color: #333;
  min-width: 60px;
}

.entry-date {
  color: #888;
  font-size: 13px;
  min-width: 0;
  white-space: normal;
}

.entry-date :deep(*) {
  white-space: normal;
  overflow-wrap: anywhere;
}

.entry-user {
  color: #2aabee;
  font-size: 13px;
}

.entry-description {
  margin-top: 4px;
  color: #666;
  font-size: 13px;
}

.entry-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
}

@media (max-width: 480px) {
  .entry-item {
    padding-right: 10px;
  }

  .entry-main {
    padding-right: 0;
  }

  .entry-actions {
    position: static;
    margin-top: 6px;
    justify-content: flex-end;
  }
}

.btn-icon {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  font-size: 14px;
  opacity: 0.6;
}

.btn-icon:hover {
  opacity: 1;
}

.empty-state {
  color: #888;
  font-size: 13px;
  padding: 12px 0;
}

.time-summary-header {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
}

.time-badge-label {
  font-size: 11px;
  color: #888;
  margin-right: 2px;
}

.time-badge-value {
  font-weight: 600;
  color: #333;
}

.entries-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 13px;
}

.section-title {
  font-weight: 600;
  color: #333;
}

.section-total {
  font-weight: 600;
  color: #2aabee;
}

.entry-item-this-task {
  padding-left: 12px;
  border-left: 2px solid #2aabee;
}

.children-time-summary {
  margin-top: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  border-left: 3px solid #888;
}

.children-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
}

.children-entries-list {
  margin-top: 8px;
}
</style>
